/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import moment from "moment";
import { useManageIPMSContext } from "./ManageIPMSContex";
import {
  GoalsAssessmentContent,
  GoalsAssessmentContentProps,
} from "./goalsAssessment";

export interface AssessmentModalState {
  /** Quarter number string — "1", "2", "3", "4", or "1,2,3,4" */
  id: string;
  /** e.g. "q1", "q2" … */
  quarterName: string;
  employeeID: number;
  employeeName: string;
  assessmentData: { start: string; end: string };
  submissionstatus?: number;
}

interface AssessmentModalProps {
  modalState: AssessmentModalState | null;
  onClose: () => void;
}

const AssessmentModal: React.FC<AssessmentModalProps> = ({
  modalState,
  onClose,
}) => {
  const { GetEmployeeGoalAssessment } = useManageIPMSContext();

  // Fetch assessment data when modal opens
  React.useEffect(() => {
    if (!modalState?.employeeID || !modalState?.id) return;
    const payload = {
      employee_id: modalState.employeeID,
      assessment_year: moment(modalState.assessmentData.start).year(),
      quarter: Number(modalState.id),
    };
    GetEmployeeGoalAssessment(payload);
  }, [modalState?.employeeID, modalState?.id]);

  // Close on Escape key
  React.useEffect(() => {
    if (!modalState) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [modalState, onClose]);

  if (!modalState) return null;

  const contentProps: GoalsAssessmentContentProps = {
    id: modalState.id,
    quarterName: modalState.quarterName,
    employeeID: modalState.employeeID,
    assessmentData: modalState.assessmentData,
    formType: "ViewOnly", // no column has ViewOnly in formTypes → all inputs disabled
    submissionstatus: modalState.submissionstatus,
    isViewOnly: true, // hides all action buttons
  };

  return (
    // Overlay — starts below the app navbar
    <div
      className="fixed top-[70px] inset-x-0 bottom-0 z-50 flex flex-col bg-white overflow-hidden"
      role="dialog"
      aria-modal="true"
    >
      {/* Sticky header */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 py-3 bg-white border-b border-gray-200 shadow-sm z-50">
        <div>
          <p className="text-sm font-semibold text-gray-800">
            Employee:{" "}
            <span className="text-blue-600">{modalState.employeeName}</span>
            {" · "}ID: {modalState.employeeID}
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Assessment Period:{" "}
            {moment(modalState.assessmentData.start).format("DD/MM/YYYY")} –{" "}
            {moment(modalState.assessmentData.end).format("DD/MM/YYYY")}
            {" · "}Quarter: {modalState.quarterName.toUpperCase()}
          </p>
        </div>
        <button
          onClick={onClose}
          className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors"
          title="Close (Esc)"
        >
          <XMarkIcon className="w-4 h-4" />
          Close
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 pb-8">
        <GoalsAssessmentContent {...contentProps} />
      </div>
    </div>
  );
};

export default AssessmentModal;
